import React, { useState } from 'react';
import { AlertCircle, Info } from 'lucide-react';

const StageMappingEditor = ({ config, onChange }) => {
  const stages = config.stages || [];
  const kanbanStages = config.kanbanStages?.opportunity || [];
  const mapping = config.opportunityStageMapping || {};

  const handleMappingChange = (stageId, selectedValues) => {
    const updatedMapping = {
      ...mapping,
      [stageId]: selectedValues
    };

    // Remove empty arrays
    Object.keys(updatedMapping).forEach(key => {
      if (!updatedMapping[key] || updatedMapping[key].length === 0) {
        delete updatedMapping[key];
      }
    });

    onChange({
      ...config,
      opportunityStageMapping: updatedMapping
    });
  };

  const getAvailableKanbanValues = () => {
    return kanbanStages.map(s => s.value);
  };

  const getStageDisplayName = (stage) => {
    return stage.displayName || stage.id;
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
          Stage Mapping
        </h2>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Map pipeline stages to opportunity stage values. This determines which opportunities appear in each pipeline stage.
        </p>
      </div>

      {kanbanStages.length === 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
              No opportunity stages configured
            </p>
            <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
              Please configure opportunity stages in the "Opportunity Stages" tab first.
            </p>
          </div>
        </div>
      )}

      {stages.length === 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
              No pipeline stages configured
            </p>
            <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
              Please configure pipeline stages in the "Stage Management" tab first.
            </p>
          </div>
        </div>
      )}

      {stages.length > 0 && kanbanStages.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm">
          <div className="divide-y divide-slate-200 dark:divide-slate-700">
            {stages.map((stage) => {
              const currentValues = mapping[stage.id] || [];
              const availableValues = getAvailableKanbanValues();

              return (
                <div key={stage.id} className="p-4">
                  <div className="mb-3">
                    <label className="block text-sm font-semibold text-slate-900 dark:text-slate-100 mb-1">
                      {getStageDisplayName(stage)}
                      {stage.displayNameTH && (
                        <span className="text-slate-500 dark:text-slate-400 ml-2">
                          ({stage.displayNameTH})
                        </span>
                      )}
                    </label>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Stage ID: {stage.id}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-2">
                      {availableValues.map((value) => {
                        const kanbanStage = kanbanStages.find(s => s.value === value);
                        const isSelected = currentValues.includes(value);
                        
                        return (
                          <button
                            key={value}
                            onClick={() => {
                              const newValues = isSelected
                                ? currentValues.filter(v => v !== value)
                                : [...currentValues, value];
                              handleMappingChange(stage.id, newValues);
                            }}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                              isSelected
                                ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-500 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-600'
                                : 'bg-slate-100 text-slate-700 border-2 border-transparent hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600'
                            }`}
                            title={kanbanStage?.label || value}
                          >
                            {kanbanStage?.label || value}
                          </button>
                        );
                      })}
                    </div>

                    {currentValues.length > 0 && (
                      <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                        Selected: {currentValues.join(', ')}
                      </div>
                    )}

                    {currentValues.length === 0 && (
                      <div className="mt-2 text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                        <Info className="h-3 w-3" />
                        No stages mapped. Opportunities won't appear in this pipeline stage.
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800 dark:text-blue-300">
            <p className="font-medium mb-1">How Stage Mapping Works:</p>
            <ul className="list-disc list-inside space-y-1 text-blue-700 dark:text-blue-400">
              <li>Select one or more opportunity stage values for each pipeline stage</li>
              <li>Opportunities with matching stage values will appear in the corresponding pipeline stage</li>
              <li>An opportunity can appear in multiple pipeline stages if its value is mapped to multiple stages</li>
              <li>If no stages are mapped, opportunities won't appear in that pipeline stage</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StageMappingEditor;

